import * as React from 'react';
import { Navigate } from 'react-router-dom';
import { assertNever } from '../../amorg-common';
import { IncidentListing, IncidentPriority, IncidentStatus } from '../../amorg-common/DTOs/Incident';
import { incidentClientApi } from '../../api/incidentClientApi';
import { colors } from '../../designSystem/colors/color';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { Icon } from '../../designSystem/icons/Icons';
import { OnOff } from '../../designSystem/inputs/OnOff';
import { Label } from '../../designSystem/Label';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { PageHeader } from '../../designSystem/PageHeader';
import { Table, TableHeaderWithSort, TableRowWithOnClick } from '../../designSystem/Table';
import { ToggletipTheOne } from '../../designSystem/ToggletipTheOne';
import { P } from '../../designSystem/typography/Body';
import { txt } from '../../txt';
import { clientAccessHelper } from '../../utils/clientAccessHelper';
import { toDateTimeString } from '../../utils/date';
import { ProtectedPageContext } from '../_contexts/ProtectedPageContext';
import { MemberPageWrapper } from '../_pageWrappers/MemberPageWrapper';
import { SingleColumnLayout } from '../_layouts/SingleColumnLayout';

export function IncidentListPage(): JSX.Element
{
    return (
        <MemberPageWrapper selectedMenuItemKey={'incidents'} breadCrumbs={[{ text: 'Indberetninger', url: '' }]}>
            
            <IncidentListPageContent />
            
        </MemberPageWrapper>
    );
}

function IncidentListPageContent (): JSX.Element
{
    const [loading, setLoading] = React.useState(true);
    const [incidents, setIncidents] = React.useState<IncidentListing[]>([]);
    const [sort, setSort] = React.useState<'status' | 'priority' | 'created'>('status');
    const [sortAscending, setSortAscending] = React.useState(true);
    const [goToIncidentId, setGotoIncidentId] = React.useState<string | null>(null);
    const [showHidden, setShowHidden] = React.useState(false);

    const ctx = React.useContext(ProtectedPageContext);
    const activeUser = ctx.user;
    const hasAccess = clientAccessHelper.canAccessIncidents(activeUser);

    function statusToSortValue(status:IncidentStatus):number
    {
        switch(status)
        {
            case 'new': return 0;
            case 'in-progress': return 1;
            case 'resolved': return 2;
        }
    }

    React.useEffect(() =>
    {
        const getData = async () => {
            setLoading(true);

            await loadIncidents();

            setLoading(false);
        };
        getData();
    }, []);

    async function loadIncidents()
    {
        const incidents = await incidentClientApi.getIncidents();
        incidents.sort((a, b) => {
            const sortA = statusToSortValue(a.status);
            const sortB = statusToSortValue(b.status);
            if(sortA !== sortB)                    
                return sortA - sortB;
            else                    
                return b.created.getTime() - a.created.getTime();
        });
        setIncidents(incidents);
    }

    function sortIncidents(sortBy:'status' | 'priority' | 'created')
    {
        const nextAscending = sortBy === sort ? !sortAscending : true;
        const multiplier = nextAscending ? 1 : -1;

        if(sortBy === 'priority')
        {
            setIncidents([...incidents].sort((a,b) => a.priority.localeCompare(b.priority) * multiplier));
            setSort('priority');
            setSortAscending(nextAscending);
        }

        if(sortBy === 'status')
        {
            setIncidents([...incidents].sort((a,b) => (statusToSortValue(a.status) - statusToSortValue(b.status)) * multiplier));
            setSort('status');
            setSortAscending(nextAscending);
        }

        if(sortBy === 'created')
        {
            setIncidents([...incidents].sort((a,b) => (a.created.getTime() - b.created.getTime()) * multiplier));
            setSort('created');
            setSortAscending(nextAscending);
        }
    }

    async function handleToggleHidden(incident: IncidentListing, hidden: boolean)
    {
        await incidentClientApi.toggleIncidentHidden(incident.id, hidden);
        await loadIncidents();
    }

    const visibleIncidents = showHidden ? incidents : incidents.filter(i => !i.hidden);

    return (


        <>
            <PageHeader
                headerIcon='record_voice_over'
                headerText='Indberetninger' />

        <SingleColumnLayout>
                <>
                    
                    {loading 
                        ? <LoadingPageOverlay loading={loading} />
                        : !hasAccess 
                            ?
                                <P>
                                    Du har ikke adgang til at se indberetninger. Kontakt din administrator hvis du mener dette er en fejl.
                                </P>
                            :
                                <>
                                    {goToIncidentId && 
                                        <Navigate to={`/incidents/${goToIncidentId}`} />
                                    }

                                    <Flex justifyContent='flex-end' alignItems='center' gap={8}>
                                        <Label>Vis alle indberetninger</Label>                                        
                                        <OnOff active={showHidden} onClick={setShowHidden} />
                                    </Flex>

                                    <Table>
                                        <thead>
                                            <tr>
                                                <th>Emne</th>
                                                <TableHeaderWithSort width='200px' onClick={() => sortIncidents('status')} text='Status' isActiveSort={sort === 'status'} ascending={sortAscending} />
                                                <TableHeaderWithSort width='150px' onClick={() => sortIncidents('priority')} text='Prioritet' isActiveSort={sort === 'priority'} ascending={sortAscending} />
                                                <TableHeaderWithSort width='150px' onClick={() => sortIncidents('created')} text='Oprettet' isActiveSort={sort === 'created'} ascending={sortAscending} />
                                                <th style={{width: '80px'}}>Skjult</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleIncidents.map(incident => {
                                                return (
                                                    <TableRowWithOnClick key={incident.id} onClick={() => setGotoIncidentId(incident.id)}>
                                                        <td>
                                                            <Flex alignItems='center' gap={4}>
                                                                <span>{incident.title}</span>
                                                                
                                                                {incident.anonymousSelected && 
                                                                    <ToggletipTheOne icon="domino_mask" iconSize="x-small" iconColor={colors.icon.active} >
                                                                        Anononym indberetter
                                                                    </ToggletipTheOne>
                                                                }
                                                                {incident &&
                                                                    <ToggletipTheOne icon="interface_actions_mail" iconSize="x-small" iconColor={colors.icon.active} >
                                                                        Kan besvares
                                                                    </ToggletipTheOne>
                                                                }
                                                            </Flex>
                                                        </td>
                                                        <td style={{fontSize: '14px'}}><StatusText status={incident.status} /></td>
                                                        <td style={{fontSize: '14px'}}><PriorityText priority={incident.priority} /></td>
                                                        <td style={{fontSize: '14px'}}>{toDateTimeString(incident.created)}</td>
                                                        <td onClick={e => e.stopPropagation()}>
                                                            <OnOff  active={incident.hidden}
                                                                onClick={() => handleToggleHidden(incident, !incident.hidden)} />
                                                        </td>
                                                    </TableRowWithOnClick>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </>
                    }
                </>
            </SingleColumnLayout>
        </>
    );
}

function StatusText({status}: {status: IncidentStatus}):JSX.Element
{
    function getIcon(status:IncidentStatus):JSX.Element
    {   
        switch(status)
        {
            case 'new': return <Icon name='status_empty' size='x-small' color={colors.icon.default} />;
            case 'in-progress': return <Icon name='clock' size='x-small' color={colors.icon.active} />;
            case 'resolved': return <Icon name='status_ok' size='x-small' color={colors.icon.success} />;

            default: assertNever(status);
        }
    }

    return <Flex alignItems='center' gap={4}>{getIcon(status)} {txt.incidentStatusText(status)}</Flex>;
}

function PriorityText({priority}: {priority: IncidentPriority}):JSX.Element
{
    function getIcon(priority:IncidentPriority):JSX.Element
    {   
        switch(priority)
        {
            case '0 - unassigned': return <Icon name='survey_question' size='x-small' color={colors.icon.default} />;
            case '1 - low': return <Icon name='caret_caretDown' size='x-small' color={colors.icon.active} />;
            case '2 - normal': return <Icon name='horizontal_rule' size='x-small' color={colors.icon.default} />;
            case '3 - high': return <Icon name='caret_caretUp' size='x-small' color={colors.icon.active} />;
            case '4 - urgent': return <Icon name='warning' size='x-small' color={colors.icon.warning} />;

            default: assertNever(priority);
        }
    }

    return <Flex alignItems='center' gap={4}>{getIcon(priority)} {txt.incidentPriorityText(priority)}</Flex>;
}